import {ReactElement} from "react";
import {EditorGroup, EditorGroupTitle} from "../EditorGroup";
import {FormGroup} from "components/UIComponents/FormGroup";
import {FormattedMessage, useIntl} from "react-intl";
import {FormInput} from "components/UIComponents/FormInput";
import {SliderToggle} from "components/UIComponents/SliderToggle";
import {EditorItem, isLeafletDocument} from "../../editorUtils";
import {getMaxWineTextLength, MAX_WINE_NAME_LENGTH, WineItem} from "../../MenuDocumentModel";
import {menuEditorActions, menuEditorSelectors} from "../../editorSlice";
import {assoc} from "ramda";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {BadgeEditor} from "./BadgeEditor";
import {FormIntInput} from "../../../../components/UIComponents/FormIntInput";
import {ShowAppellationControl} from "./editor/ShowAppellationControl";
import {ShowYearControl} from "./editor/ShowYearControl";

export interface WineAppWineEditorProps {
    wine: EditorItem<WineItem>
}

export function WineAppWineEditor(props: WineAppWineEditorProps): ReactElement {
    const document = useAppSelector(menuEditorSelectors.document);
    const isLeaflet = document&&isLeafletDocument(document);
    const format = document?.format || 'a4';

    const dispatch = useAppDispatch();
    const intl = useIntl();

    const itemKey = props.wine.itemKey;
    const wineItem = props.wine.menuItem;

    function updateProperty<K extends string, T>(property: K, value: T) {
        dispatch(menuEditorActions.editItem({
            key: itemKey,
            newItem: assoc<T, WineItem, K>(property, value, wineItem) as unknown as WineItem
        }));
    }

    return (
        <div className="flex flex-col items-stretch">
            <EditorGroup>
                <FormGroup
                    groupTitle={<FormattedMessage id="editor.wine.name"/>}
                    error={wineItem.name.length > MAX_WINE_NAME_LENGTH ? intl.formatMessage({id: 'editor.wine.name.warningTooLong'}) : ''}
                    chars={wineItem.name.length}
                    maxChars={MAX_WINE_NAME_LENGTH}
                >
                    <FormInput
                        value={wineItem.name}
                        onChange={e => updateProperty('name', e.target.value.substring(0, 39))}
                        hasError={wineItem.name.length > MAX_WINE_NAME_LENGTH}
                    />
                </FormGroup>

                {isLeaflet&&
                    <FormGroup groupTitle={<FormattedMessage id="editor.wine.title"/>}>
                        <textarea
                            className={`py-3 pl-4 pr-2 w-full
                            text-sm
                            rounded border border-gray-1 outline-0
                            focus:border-focus h-[85px]`}
                            value={wineItem.title}
                            onChange={e => updateProperty('title', e.target.value)}
                        />
                    </FormGroup>
                }

                <div className="flex gap-10 items-stretch">
                    <FormGroup groupTitle={<FormattedMessage id="editor.wine.year"/>}>
                        <FormIntInput
                            intValue={wineItem.year ?? null}
                            type="number"
                            onChange={x => updateProperty('year', x)}
                        />
                    </FormGroup>
                    <div className="opacity-50">
                        <FormGroup groupTitle={<FormattedMessage id="editor.wine.articleNo"/>}>
                            <FormInput
                                value={wineItem.objectID ?? ""}
                                readOnly={true}
                                onChange={e => updateProperty('objectID', e.target.value)}
                            />
                        </FormGroup>
                    </div>
                </div>

                <ShowYearControl wine={props.wine}/>
            </EditorGroup>

            <EditorGroup>
                <EditorGroupTitle>
                    <FormattedMessage id="editor.wine.description"/>
                </EditorGroupTitle>

                {!isLeaflet&&<div className="flex items-center gap-2">
                    <div className="flex-auto text-sm">
                        <FormattedMessage id="editor.wine.details"/>
                    </div>
                    <div className="flex-none h-7 w-12">
                        <SliderToggle
                            enabled={wineItem.detailed}
                            setEnabled={enabled => updateProperty('detailed', enabled)}
                        />
                    </div>
                </div>}

                {(wineItem.detailed||isLeaflet) && <>
                    {!isLeaflet&&<div className="flex items-center gap-2">
                        <span className="flex-auto text-sm">
                            <FormattedMessage id="editor.wine.showDescription"/>
                        </span>
                        <div className="flex-none h-7 w-12">
                            <SliderToggle
                                enabled={wineItem.showDescription}
                                setEnabled={showDescription => updateProperty('showDescription', showDescription)}
                            />
                        </div>
                    </div>}

                    {(wineItem.showDescription||isLeaflet) && <FormGroup
                        groupTitle=""
                        error={wineItem.text.length > getMaxWineTextLength(format) ? intl.formatMessage({id: 'editor.wine.description.warningTooLong'}) : ''}
                        chars={wineItem.text.length}
                        maxChars={getMaxWineTextLength(format)}
                    >
                        <textarea
                            className={`py-3 pl-4 pr-2 w-full
                            text-sm
                            rounded border border-gray-1 outline-0
                            focus:border-focus h-[85px]  ${wineItem.text.length > getMaxWineTextLength(format) ? 'border-primary' : ''}`}
                            value={wineItem.text}
                            onChange={e => updateProperty('text', e.target.value)}
                        />
                    </FormGroup>}

                    <div className="flex items-center gap-2">
                        <span className="flex-auto text-sm">
                            <FormattedMessage id="editor.wine.showTasteSweetness"/>
                        </span>
                        <div className="flex-none h-7 w-12">
                            <SliderToggle
                                enabled={wineItem.showTasteSweetness !== undefined && wineItem.showTasteSweetness !== null ? wineItem.showTasteSweetness : true}
                                setEnabled={showTasteSweetness => updateProperty('showTasteSweetness', showTasteSweetness)}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="flex-auto text-sm">
                            <FormattedMessage id="editor.wine.showSuitableFood"/>
                        </span>
                        <div className="flex-none h-7 w-12">
                            <SliderToggle
                                enabled={wineItem.showSuitableFood !== undefined && wineItem.showSuitableFood !== null ? wineItem.showSuitableFood : true}
                                setEnabled={showSuitableFood => updateProperty('showSuitableFood', showSuitableFood)}
                            />
                        </div>
                    </div>

                    {(wineItem.showSuitableFood === undefined || wineItem.showSuitableFood === null || wineItem.showSuitableFood) && <FormGroup groupTitle={<></>}>
                        <FormInput
                            value={wineItem.suitableFood}
                            onChange={e => updateProperty('suitableFood', e.target.value)}
                        />
                    </FormGroup>}

                    <ShowAppellationControl wine={props.wine}/>
                </>}
            </EditorGroup>

            <BadgeEditor wine={props.wine} />
        </div>
    );
}

